import { Fragment, useEffect, useRef, useState } from 'react';

import { GripVertical, Pencil, Trash2 } from 'lucide-react';

import { useAuth } from '@chainlit/react-client';

function linkifyText(text: string) {
  const urlRegex = /https?:\/\/[^\s]+/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = urlRegex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    let url = match[0];
    const trailing = url.match(/[.,;:!?)"']+$/);
    if (trailing) url = url.slice(0, url.length - trailing[0].length);
    parts.push(
      <a
        key={match.index}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary underline underline-offset-2 hover:text-primary/80 break-all"
      >
        {url}
      </a>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length === 0 ? text : <>{parts}</>;
}


interface Prayer {
  id: number;
  what: string;
  created_at: string;
}

interface PrayerPerson {
  who: string;
  prayers: Prayer[];
}

function formatDate(label: string): string {
  return label;
}

function DeleteConfirmDialog({
  title = 'Delete prayer?',
  label,
  onConfirm,
  onCancel
}: {
  title?: string;
  label: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onMouseDown={(e) => e.target === e.currentTarget && onCancel()}
    >
      <div className="bg-background border border-border rounded-xl shadow-xl w-full max-w-sm mx-4 overflow-hidden">
        <div className="px-4 py-3 border-b border-border/60">
          <span className="text-sm font-semibold">{title}</span>
        </div>
        <div className="p-4 flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            Delete{' '}
            <span className="text-foreground font-medium">"{label}"</span>?
            This cannot be undone.
          </p>
          <div className="flex gap-2 justify-end">
            <button
              onClick={onCancel}
              className="px-3 py-1.5 text-sm rounded-lg border border-border hover:bg-muted/40 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="px-3 py-1.5 text-sm rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EditPersonModal({
  who,
  onClose,
  onSaved
}: {
  who: string;
  onClose: () => void;
  onSaved: (oldWho: string, newWho: string) => void;
}) {
  const [name, setName] = useState(who);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || name.trim() === who) { onClose(); return; }
    setSaving(true);
    setError('');
    try {
      const res = await fetch('/api/prayers/edit-person', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ old_who: who, new_who: name.trim() })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Failed to save');
        setSaving(false);
        return;
      }
      onSaved(who, name.trim());
      onClose();
    } catch {
      setError('Network error');
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-background border border-border rounded-xl shadow-xl w-full max-w-sm mx-4 overflow-hidden">
        <div className="px-4 py-3 border-b border-border/60">
          <span className="text-sm font-semibold">Edit person</span>
        </div>
        <form onSubmit={handleSubmit} className="p-4 flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Name</label>
            <input
              ref={inputRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border border-border bg-muted/30 px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-primary/50"
              required
            />
          </div>
          {error && <p className="text-xs text-destructive">{error}</p>}
          <div className="flex gap-2 justify-end pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm rounded-lg border border-border hover:bg-muted/40 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !name.trim()}
              className="px-3 py-1.5 text-sm rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditPrayerModal({
  prayer,
  onClose,
  onSaved
}: {
  prayer: Prayer;
  onClose: () => void;
  onSaved: (id: number, what: string) => void;
}) {
  const [what, setWhat] = useState(prayer.what);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!what.trim() || what.trim() === prayer.what) { onClose(); return; }
    setSaving(true);
    setError('');
    try {
      const res = await fetch('/api/prayers/edit', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: prayer.id, what: what.trim() })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Failed to save');
        setSaving(false);
        return;
      }
      onSaved(prayer.id, what.trim());
      onClose();
    } catch {
      setError('Network error');
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-background border border-border rounded-xl shadow-xl w-full max-w-sm mx-4 overflow-hidden">
        <div className="px-4 py-3 border-b border-border/60">
          <span className="text-sm font-semibold">Edit prayer</span>
        </div>
        <form onSubmit={handleSubmit} className="p-4 flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Prayer</label>
            <textarea
              ref={inputRef}
              value={what}
              onChange={(e) => setWhat(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              rows={4}
              className="w-full rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary/50 resize-y leading-snug"
              required
            />
          </div>
          {error && <p className="text-xs text-destructive">{error}</p>}
          <div className="flex gap-2 justify-end pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm rounded-lg border border-border hover:bg-muted/40 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !what.trim()}
              className="px-3 py-1.5 text-sm rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function dropPosition(e: React.DragEvent): 'before' | 'after' {
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
  return e.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
}

function PrayerRow({
  prayer,
  onEdit,
  onDelete,
  dropIndicator,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop
}: {
  prayer: Prayer;
  onEdit: (prayer: Prayer) => void;
  onDelete: (id: number) => void;
  dropIndicator?: 'before' | 'after' | null;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
}) {
  const [confirming, setConfirming] = useState(false);

  return (
    <li
      className="group relative"
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {dropIndicator === 'before' && (
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary/80 z-20 pointer-events-none" />
      )}
      {dropIndicator === 'after' && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary/80 z-20 pointer-events-none" />
      )}
      <div className="flex items-start hover:bg-muted/40 transition-colors relative">
        <span className="absolute left-0 top-0 bottom-0 flex items-center pl-1 opacity-0 group-hover:opacity-100 cursor-grab text-muted-foreground/30 z-10">
          <GripVertical size={12} />
        </span>
        <div className="flex items-start gap-2.5 px-3 py-1.5 flex-1 min-w-0">
          <span className="w-3 shrink-0 text-border select-none mt-[2px]">└</span>
          <span className="flex flex-col min-w-0">
            <span className="text-sm leading-snug text-muted-foreground">
              {linkifyText(prayer.what)}
            </span>
            <span className="text-xs text-muted-foreground/50 leading-snug mt-0.5">
              {formatDate(prayer.created_at)}
            </span>
          </span>
        </div>
        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5 shrink-0 mt-1.5 mr-2">
          <button
            onClick={() => onEdit(prayer)}
            title="Edit prayer"
            className="p-1 rounded text-muted-foreground/60 hover:text-foreground hover:bg-muted/50"
          >
            <Pencil size={13} />
          </button>
          <button
            onClick={() => setConfirming(true)}
            title="Delete prayer"
            className="p-1 rounded text-muted-foreground/60 hover:text-destructive hover:bg-destructive/10"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>
      {confirming && (
        <DeleteConfirmDialog
          label={prayer.what}
          onConfirm={() => {
            setConfirming(false);
            onDelete(prayer.id);
          }}
          onCancel={() => setConfirming(false)}
        />
      )}
    </li>
  );
}

function PersonRow({
  who,
  onEdit,
  onAddPrayer,
  onDelete,
  dropIndicator,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop
}: {
  who: string;
  onEdit: (who: string) => void;
  onAddPrayer: (who: string) => void;
  onDelete: (who: string) => void;
  dropIndicator?: 'before' | 'after' | null;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
}) {
  const [confirming, setConfirming] = useState(false);

  return (
    <li
      className="group relative"
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {dropIndicator === 'before' && (
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary/80 z-20 pointer-events-none" />
      )}
      {dropIndicator === 'after' && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary/80 z-20 pointer-events-none" />
      )}
      <div className="flex items-center hover:bg-muted/40 transition-colors px-3 py-1.5 relative">
        <span className="absolute left-0 top-0 bottom-0 flex items-center pl-1 opacity-0 group-hover:opacity-100 cursor-grab text-muted-foreground/30 z-10">
          <GripVertical size={12} />
        </span>
        <span className="flex-1 text-sm font-medium text-foreground">{who}</span>
        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
          <button
            onClick={() => onEdit(who)}
            title={`Rename ${who}`}
            className="p-1 rounded text-muted-foreground/60 hover:text-foreground hover:bg-muted/50"
          >
            <Pencil size={13} />
          </button>
          <button
            onClick={() => setConfirming(true)}
            title={`Delete ${who}`}
            className="p-1 rounded text-muted-foreground/60 hover:text-destructive hover:bg-destructive/10"
          >
            <Trash2 size={13} />
          </button>
          <button
            onClick={() => onAddPrayer(who)}
            title={`Add prayer for ${who}`}
            className="w-5 h-5 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 text-base leading-none"
          >
            +
          </button>
        </div>
      </div>
      {confirming && (
        <DeleteConfirmDialog
          title="Delete person?"
          label={who}
          onConfirm={() => { setConfirming(false); onDelete(who); }}
          onCancel={() => setConfirming(false)}
        />
      )}
    </li>
  );
}

function AddPrayerModal({
  people,
  initialWho,
  onClose,
  onAdded
}: {
  people: string[];
  initialWho: string | null;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [who, setWho] = useState(initialWho ?? '');
  const [what, setWhat] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const whoRef = useRef<HTMLInputElement>(null);
  const whatRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (initialWho) {
      whatRef.current?.focus();
    } else {
      whoRef.current?.focus();
    }
  }, [initialWho]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!who.trim() || !what.trim()) return;
    setSaving(true);
    setError('');
    try {
      const res = await fetch('/api/prayers/add', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ who: who.trim(), what: what.trim() })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Failed to add prayer');
        setSaving(false);
        return;
      }
      onAdded();
      onClose();
    } catch {
      setError('Network error');
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-background border border-border rounded-xl shadow-xl w-full max-w-sm mx-4 overflow-hidden">
        <div className="px-4 py-3 border-b border-border/60">
          <span className="text-sm font-semibold">Add prayer</span>
        </div>
        <form onSubmit={handleSubmit} className="p-4 flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Person</label>
            <input
              ref={whoRef}
              list="prayer-people-list"
              type="text"
              value={who}
              onChange={(e) => setWho(e.target.value)}
              placeholder="Person's name"
              readOnly={!!initialWho}
              className={
                'w-full rounded-lg border border-border bg-muted/30 px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-primary/50' +
                (initialWho ? ' opacity-60 cursor-default' : '')
              }
              required
            />
            {people.length > 0 && !initialWho && (
              <datalist id="prayer-people-list">
                {people.map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Prayer</label>
            <textarea
              ref={whatRef}
              value={what}
              onChange={(e) => setWhat(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              placeholder="What to pray for"
              rows={4}
              className="w-full rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary/50 resize-y leading-snug"
              required
            />
          </div>
          {error && <p className="text-xs text-destructive">{error}</p>}
          <div className="flex gap-2 justify-end pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm rounded-lg border border-border hover:bg-muted/40 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !who.trim() || !what.trim()}
              className="px-3 py-1.5 text-sm rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {saving ? 'Adding…' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

type DragState =
  | { kind: 'person'; who: string }
  | { kind: 'prayer'; id: number; who: string };

export default function HomePrayerList() {
  const { user } = useAuth();
  const [people, setPeople] = useState<PrayerPerson[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForWho, setAddForWho] = useState<string | null>(null);
  const [editingPerson, setEditingPerson] = useState<string | null>(null);
  const [editingPrayer, setEditingPrayer] = useState<Prayer | null>(null);

  const dragRef = useRef<DragState | null>(null);
  const [dropTarget, setDropTarget] = useState<{ key: string; position: 'before' | 'after' } | null>(null);

  const email = user?.identifier;

  const loadPrayers = () => {
    fetch('/api/prayers', { method: 'POST', credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data: PrayerPerson[]) => setPeople(data))
      .catch(() => {});
  };

  useEffect(() => {
    if (!email) return;
    loadPrayers();
  }, [email]);

  const handleDelete = (id: number) => {
    setPeople((prev) =>
      prev
        .map((p) => ({ ...p, prayers: p.prayers.filter((pr) => pr.id !== id) }))
        .filter((p) => p.prayers.length > 0)
    );
    fetch('/api/prayers/delete', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id })
    }).catch(() => loadPrayers());
  };

  const handlePersonSaved = (oldWho: string, newWho: string) => {
    setPeople((prev) =>
      prev.map((p) =>
        p.who === oldWho ? { ...p, who: newWho } : p
      )
    );
  };

  const handleDeletePerson = (who: string) => {
    const person = people.find((p) => p.who === who);
    if (!person) return;
    setPeople((prev) => prev.filter((p) => p.who !== who));
    Promise.all(
      person.prayers.map((pr) =>
        fetch('/api/prayers/delete', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: pr.id })
        })
      )
    ).catch(() => loadPrayers());
  };

  const handlePrayerSaved = (id: number, what: string) => {
    setPeople((prev) =>
      prev.map((p) => ({
        ...p,
        prayers: p.prayers.map((pr) => pr.id === id ? { ...pr, what } : pr)
      }))
    );
  };

  const handleDragEnd = () => {
    dragRef.current = null;
    setDropTarget(null);
  };

  const handlePersonDragOver = (e: React.DragEvent, who: string) => {
    e.preventDefault();
    const drag = dragRef.current;
    if (!drag || drag.kind !== 'person' || drag.who === who) { setDropTarget(null); return; }
    setDropTarget({ key: `person:${who}`, position: dropPosition(e) });
  };

  const handlePrayerDragOver = (e: React.DragEvent, id: number, who: string) => {
    e.preventDefault();
    const drag = dragRef.current;
    if (!drag || drag.kind !== 'prayer' || drag.id === id || drag.who !== who) { setDropTarget(null); return; }
    setDropTarget({ key: `prayer:${id}`, position: dropPosition(e) });
  };

  const handlePersonDrop = (e: React.DragEvent, who: string) => {
    e.preventDefault();
    const drag = dragRef.current;
    if (!drag || drag.kind !== 'person' || drag.who === who) return;
    const fromWho = drag.who;
    const position = dropPosition(e);
    dragRef.current = null;
    setDropTarget(null);

    const fromIdx = people.findIndex((p) => p.who === fromWho);
    if (fromIdx < 0) return;
    const moved = people[fromIdx];
    const rest = people.filter((_, i) => i !== fromIdx);
    const targetIdx = rest.findIndex((p) => p.who === who);
    if (targetIdx < 0) return;
    const insertIdx = position === 'after' ? targetIdx + 1 : targetIdx;
    const newPeople = [...rest.slice(0, insertIdx), moved, ...rest.slice(insertIdx)];
    setPeople(newPeople);
    fetch('/api/prayers/reorder-people', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ordered_who: newPeople.map((p) => p.who) })
    }).catch(() => loadPrayers());
  };

  const handlePrayerDrop = (e: React.DragEvent, id: number, who: string) => {
    e.preventDefault();
    const drag = dragRef.current;
    if (!drag || drag.kind !== 'prayer' || drag.id === id || drag.who !== who) return;
    const fromId = drag.id;
    const position = dropPosition(e);
    dragRef.current = null;
    setDropTarget(null);

    const newPeople = people.map((p) => {
      if (p.who !== who) return p;
      const fromIdx = p.prayers.findIndex((pr) => pr.id === fromId);
      if (fromIdx < 0) return p;
      const moved = p.prayers[fromIdx];
      const rest = p.prayers.filter((_, i) => i !== fromIdx);
      const targetIdx = rest.findIndex((pr) => pr.id === id);
      if (targetIdx < 0) return p;
      const insertIdx = position === 'after' ? targetIdx + 1 : targetIdx;
      const newPrayers = [...rest.slice(0, insertIdx), moved, ...rest.slice(insertIdx)];
      return { ...p, prayers: newPrayers };
    });
    setPeople(newPeople);

    const person = newPeople.find((p) => p.who === who);
    if (person) {
      fetch('/api/prayers/reorder', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ordered_ids: person.prayers.map((pr) => pr.id) })
      }).catch(() => loadPrayers());
    }
  };

  if (!email) return null;

  return (
    <>
      <div className="w-full rounded-xl border border-border bg-card overflow-hidden shadow-sm">
        <div className="px-3 py-2 border-b border-border/60 flex items-center justify-between">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Prayer List
          </span>
          <button
            onClick={() => { setAddForWho(null); setShowAddModal(true); }}
            title="Add prayer"
            className="w-5 h-5 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors text-base leading-none"
          >
            +
          </button>
        </div>
        {!people.length && (
          <p className="px-3 py-3 text-xs text-muted-foreground/60">
            Tap <span className="font-semibold">+</span> to add a prayer.
          </p>
        )}
        <ul className="divide-y divide-border/40">
          {people.map((person) => (
            <Fragment key={person.who}>
              <PersonRow
                who={person.who}
                onEdit={setEditingPerson}
                onAddPrayer={(who) => { setAddForWho(who); setShowAddModal(true); }}
                onDelete={handleDeletePerson}
                dropIndicator={dropTarget?.key === `person:${person.who}` ? dropTarget.position : null}
                onDragStart={() => { dragRef.current = { kind: 'person', who: person.who }; }}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handlePersonDragOver(e, person.who)}
                onDrop={(e) => handlePersonDrop(e, person.who)}
              />
              {person.prayers.map((prayer) => (
                <PrayerRow
                  key={prayer.id}
                  prayer={prayer}
                  onEdit={setEditingPrayer}
                  onDelete={handleDelete}
                  dropIndicator={dropTarget?.key === `prayer:${prayer.id}` ? dropTarget.position : null}
                  onDragStart={() => { dragRef.current = { kind: 'prayer', id: prayer.id, who: person.who }; }}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => handlePrayerDragOver(e, prayer.id, person.who)}
                  onDrop={(e) => handlePrayerDrop(e, prayer.id, person.who)}
                />
              ))}
            </Fragment>
          ))}
        </ul>
      </div>

      {showAddModal && (
        <AddPrayerModal
          people={people.map((p) => p.who)}
          initialWho={addForWho}
          onClose={() => { setShowAddModal(false); setAddForWho(null); }}
          onAdded={loadPrayers}
        />
      )}
      {editingPerson !== null && (
        <EditPersonModal
          who={editingPerson}
          onClose={() => setEditingPerson(null)}
          onSaved={(oldWho, newWho) => {
            handlePersonSaved(oldWho, newWho);
            setEditingPerson(null);
          }}
        />
      )}
      {editingPrayer !== null && (
        <EditPrayerModal
          prayer={editingPrayer}
          onClose={() => setEditingPrayer(null)}
          onSaved={(id, what) => {
            handlePrayerSaved(id, what);
            setEditingPrayer(null);
          }}
        />
      )}
    </>
  );
}
